import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, UPLOADS } from "../../config/api";

import VerticalAds from "../../Admin/Pages/VerticleAds";

export default function Profile({ isOpen, onClose }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [profileImage, setProfileImage] = useState(null);

  const [preview, setPreview] = useState("");

  const defaultForm = {
    profile_image: "",
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    role: "",
    account_type: "",
    status: "",
    provider_options: [],
    supplier_types: [],
    design_types: [],
  };

  const [form, setForm] = useState(defaultForm);

  const [supplierList, setSupplierList] = useState([]);

  const [providerRules, setProviderRules] = useState({});

  const [showRules, setShowRules] = useState(false);

  const [pendingOption, setPendingOption] = useState("");

  const [agreeChecked, setAgreeChecked] = useState(false);

  const [acceptedRules, setAcceptedRules] = useState([]);

  // ================= LOAD RULES =================

  useEffect(() => {
    fetch("/rules.json")
      .then((res) => res.json())

      .then((data) => setProviderRules(data))

      .catch(console.error);
  }, []);

  // ================= FETCH PROFILE =================

  const fetchProfile = useCallback(async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));

    if (!user) {
      onClose();

      navigate("/");

      return;
    }

    try {
      const res = await fetch(`${API.GET_USER}${user.id}`);

      const data = await res.json();

      if (data.success) {
        let providerArray = [];

        let supplierArray = [];

        let designArray = [];

        try {
          providerArray = JSON.parse(data.user?.provider_options || "[]");
        } catch {
          providerArray = [];
        }

        try {
          supplierArray = JSON.parse(data.user?.supplier_types || "[]");
        } catch {
          supplierArray = [];
        }

        try {
          designArray = JSON.parse(data.user?.design_types || "[]");
        } catch {
          designArray = [];
        }

        setForm({
          profile_image: data.user?.profile_image || "",

          name: data.user?.name || "",

          email: data.user?.email || "",

          phone: data.user?.phone || "",

          city: data.user?.city || "",

          state: data.user?.state || "",

          role: data.user?.role || "",

          account_type: data.user?.account_type || "",

          status: data.user?.status || "",

          provider_options: Array.isArray(providerArray) ? providerArray : [],

          supplier_types: Array.isArray(supplierArray) ? supplierArray : [],

          design_types: Array.isArray(designArray) ? designArray : [],
        });

        setAcceptedRules(
          Array.isArray(providerArray)
            ? providerArray.filter((x) => x !== "visitor")
            : [],
        );
      }
    } catch (err) {
      console.log(err);
    }
  }, [navigate, onClose]);

  // ================= FETCH SUPPLIER TYPES =================

  const fetchSupplierTypes = async () => {
    try {
      const res = await fetch(`${API.BASE_URL}/api.php?action=supplier_types`);

      const data = await res.json();

      if (data.status) {
        setSupplierList(data.data);
      }
    } catch (err) {
      console.log("Supplier type error", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProfile();

      fetchSupplierTypes();
    } else {
      setForm(defaultForm);

      setPreview("");

      setProfileImage(null);
    }
  }, [isOpen, fetchProfile]);

  // ================= INPUT CHANGE =================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]: value,
    }));
  };

  // ================= PROVIDER UPDATE =================

  const updateProviderOption = (value, checked) => {
    setForm((prev) => {
      let providers = [...prev.provider_options];

      let suppliers = [...prev.supplier_types];

      if (value === "visitor") {
        providers = checked ? ["visitor"] : [];

        suppliers = [];
      } else {
        providers = providers.filter((x) => x !== "visitor");

        if (checked) {
          if (!providers.includes(value)) {
            providers.push(value);
          }
        } else {
          providers = providers.filter((x) => x !== value);

          if (value === "supplier") {
            suppliers = [];
          }

          setAcceptedRules((prevRules) => prevRules.filter((x) => x !== value));
        }
      }

      return {
        ...prev,

        provider_options: providers,

        supplier_types: suppliers,

        account_type:
          providers.length === 0 || providers.includes("visitor")
            ? "customer"
            : "provider",
      };
    });
  };

  const handleProviderChange = (e) => {
    const { value, checked } = e.target;

    if (value === "visitor") {
      updateProviderOption(value, checked);

      return;
    }

    const ruleData = providerRules[value];

    const hasRules =
      ruleData &&
      ((Array.isArray(ruleData.rules) && ruleData.rules.length > 0) ||
        (Array.isArray(ruleData.sections) && ruleData.sections.length > 0));

    if (checked && hasRules && !acceptedRules.includes(value)) {
      setPendingOption(value);

      setAgreeChecked(false);

      setShowRules(true);

      return;
    }

    updateProviderOption(value, checked);
  };

  const handleAgreeRules = () => {
    setAcceptedRules((prev) => [...prev, pendingOption]);

    updateProviderOption(pendingOption, true);

    setPendingOption("");

    setAgreeChecked(false);

    setShowRules(false);
  };

  const handleCancelRules = () => {
    setPendingOption("");

    setAgreeChecked(false);

    setShowRules(false);
  };

  // ================= SUPPLIER CHANGE =================

  const handleSupplierTypeChange = (e) => {
    const { value, checked } = e.target;

    setForm((prev) => ({
      ...prev,

      supplier_types: checked
        ? [...prev.supplier_types, value]
        : prev.supplier_types.filter((x) => x !== value),
    }));
  };

  // ================= IMAGE =================

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setProfileImage(file);

      setPreview(URL.createObjectURL(file));
    }
  };

  // ================= SUBMIT =================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    const user = JSON.parse(sessionStorage.getItem("user"));

    try {
      const fd = new FormData();

      fd.append("id", user.id);

      fd.append("name", form.name);

      fd.append("email", form.email);

      fd.append("phone", form.phone);

      fd.append("city", form.city);

      fd.append("state", form.state);

      fd.append("role", form.role);

      fd.append("account_type", form.account_type);

      fd.append("status", form.status);

      fd.append("provider_options", JSON.stringify(form.provider_options));

      fd.append("supplier_types", JSON.stringify(form.supplier_types));

      fd.append("design_types", JSON.stringify(form.design_types));

      if (profileImage) {
        fd.append("profile_image", profileImage);
      }

      const res = await fetch(API.UPDATE_PROFILE, {
        method: "POST",
        body: fd,
      });

      const data = await res.json();

      setLoading(false);
      if (data.success || data.status) {
        const updatedUser = {
          ...user,
          ...form,
          profile_image: data.profile_image || form.profile_image,
        };

        sessionStorage.setItem("user", JSON.stringify(updatedUser));

        window.dispatchEvent(new Event("userLogin"));

        alert("Profile Updated Successfully");

        onClose();

        if (
          form.provider_options.length === 1 &&
          form.provider_options.includes("visitor")
        ) {
          window.open("/");
        } else {
          window.open("/user/dashboard", "_blank");
        }
      }
    } catch (err) {
      setLoading(false);

      console.log(err);

      alert("Failed to update profile");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="clientprofile-overlay" onClick={onClose}>
      <div
        className="clientprofile-modal1"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================= LEFT SIDEBAR ================= */}

        <div className="profile-sidebar1">
          <div className="myprofile-avatar-wrapper">
            <label htmlFor="myProfileUpload" className="myprofile-avatar-label">
              {preview ? (
                <img
                  src={preview}
                  className="myprofile-avatar-image"
                  alt="profile"
                />
              ) : form.profile_image ? (
                <img
                  src={`${UPLOADS.PROFILE_IMAGE}${form.profile_image}`}
                  className="myprofile-avatar-image"
                  alt="profile"
                />
              ) : (
                <div className="myprofile-avatar-letter">
                  {form.name ? form.name.charAt(0).toUpperCase() : "U"}
                </div>
              )}

              <span className="myprofile-edit-button">
                <i className="bi bi-pencil-fill"></i>
              </span>
            </label>

            <input
              id="myProfileUpload"
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageChange}
            />
          </div>

          <div className="badge">{form.account_type || "Customer"}</div>

          <div
            className="badge"
            style={{
              backgroundColor: "#ef4444",

              color: "#fff",

              marginTop: "8px",
            }}
          >
            {form.status || "Active"}
          </div>
          <div className="small text-center fw-bold p-1">SPONSORED</div>

          <VerticalAds />
        </div>

        {/* ================= MAIN ================= */}

        <div className="profile-main">
          <header className="profile-header">
            <div>
              <h2>Account Settings</h2>
            </div>

            <button className="close-icon-btn" onClick={onClose}>
              ✕
            </button>
          </header>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="input-box">
                <label>Full Name</label>

                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="input-box">
                <label>Email Address</label>

                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="input-box readonly">
                <label>Mobile Number</label>

                <input name="phone" value={form.phone} readOnly />
              </div>

              <div className="input-box readonly">
                <label>Designation / System Role</label>

                <input name="role" value={form.role} readOnly />
              </div>

              <div className="input-box">
                <label>City</label>

                <input name="city" value={form.city} onChange={handleChange} />
              </div>

              <div className="input-box">
                <label>State</label>

                <input
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* ================= PROVIDER OPTIONS ================= */}
            <div className="services-section">
              <span className="section-label">
                What items/services do you provide?
              </span>

              <div className="services-grid">
                {[
                  {
                    value: "visitor",
                    label: "Visitor / Customer",
                  },
                  {
                    value: "job_worker",
                    label: "Job Worker",
                  },
                  {
                    value: "sketcher",
                    label: "Sketcher",
                  },
                  {
                    value: "machines",
                    label: "Machine Provider",
                  },
                  {
                    value: "designers",
                    label: "Designer",
                  },
                  {
                    value: "designs",
                    label: "Embroidery Design Seller",
                  },
                  {
                    value: "workspaces",
                    label: "Workspace Provider",
                  },
                  {
                    value: "supplier",
                    label: "Supplier",
                  },
                ].map((item) => {
                  const checked = form.provider_options.includes(item.value);

                  return (
                    <div
                      key={item.value}
                      className={`service-item ${checked ? "selected" : ""}`}
                      onClick={() => {
                        handleProviderChange({
                          target: {
                            value: item.value,

                            checked: !checked,
                          },
                        });
                      }}
                    >
                      <div
                        className={`checkbox-sign-box ${
                          checked ? "checked" : ""
                        }`}
                      >
                        {checked && <span className="checkmark-sign">✓</span>}
                      </div>

                      <div className="label">{item.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* ================= SUPPLIER TYPES ================= */}
            {form.provider_options.includes("supplier") && (
              <div
                className="services-section"
                style={{
                  marginTop: "25px",
                }}
              >
                <span className="section-label">
                  Select Your Supplier Sub-Categories
                </span>

                <div className="services-grid">
                  {supplierList.map((sup) => {
                    const checked = form.supplier_types.includes(sup.type);

                    return (
                      <div
                        key={sup.id}
                        className={`service-item ${checked ? "selected" : ""}`}
                        onClick={() => {
                          handleSupplierTypeChange({
                            target: {
                              value: sup.type,

                              checked: !checked,
                            },
                          });
                        }}
                      >
                        <div
                          className={`checkbox-sign-box ${
                            checked ? "checked" : ""
                          }`}
                        >
                          {checked && <span className="checkmark-sign">✓</span>}
                        </div>

                        <div className="label">{sup.display_name}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* ================= SAVE BUTTON ================= */}
            <div className="form-footer">
              <button type="submit" className="save-btn" disabled={loading}>
                {loading ? (
                  <span className="loader"></span>
                ) : (
                  "Save Profile Modifications"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ================= RULES POPUP ================= */}

      {showRules && (
        <div
          className="register-popup-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="register-popup-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rules-popup-header">
              <h3>{providerRules[pendingOption]?.title}</h3>

              <button
                type="button"
                className="register-popup-close"
                onClick={handleCancelRules}
              >
                ✕
              </button>
            </div>

            <div className="rules-popup-body">
              <div className="rules-warning">
                <i className="bi bi-exclamation-triangle-fill"></i>

                <span>Please read all rules carefully.</span>
              </div>

              <ul className="rules-list">
                {providerRules[pendingOption]?.sections ? (
                  providerRules[pendingOption].sections.map(
                    (section, index) => (
                      <div key={index} className="rules-section">
                        <h4 className="rules-heading">{section.heading}</h4>

                        <ul>
                          {section.rules.map((rule, rIndex) => (
                            <li key={rIndex}>
                              <i className="bi bi-check-circle-fill"></i>

                              {rule}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ),
                  )
                ) : (
                  <ul>
                    {providerRules[pendingOption]?.rules?.map((rule, index) => (
                      <li key={index}>
                        <i className="bi bi-check-circle-fill"></i>

                        {rule}
                      </li>
                    ))}
                  </ul>
                )}
              </ul>

              <div className="rules-agreement">
                <label>
                  <input
                    type="checkbox"
                    checked={agreeChecked}
                    onChange={(e) => setAgreeChecked(e.target.checked)}
                  />

                  <span>
                    મેં ઉપર લખેલ તમામ શરતો અને નિયમો વાંચ્યા છે અને તેનું પાલન
                    કરવા માટે સંમત છું.
                  </span>
                </label>
              </div>
            </div>

            <div className="rules-popup-footer">
              <button
                type="button"
                className="rules-cancel-btn"
                onClick={handleCancelRules}
              >
                Cancel
              </button>

              <button
                type="button"
                className="rules-agree-btn"
                disabled={!agreeChecked}
                onClick={handleAgreeRules}
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
